import { useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowRight, Check, ExternalLink, SkipForward } from "lucide-react";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { NetworkQueryEditor } from "@/components/feature/network-query-editor";
import { useCreateProject } from "@/hooks/use-projects-mutations";
import { useBranding } from "@/hooks/use-branding";

export const Route = createFileRoute("/_auth/onboarding")({
  component: OnboardingWizard,
});

const PROVIDER_LABEL: Record<string, string> = {
  x: "X (Twitter)",
  meta: "Meta (Facebook + Instagram)",
  google: "Google / YouTube",
  linkedin: "LinkedIn",
  reddit: "Reddit",
  tiktok: "TikTok",
};

const projectSchema = z.object({
  name: z.string().min(2).max(160),
  description: z.string().max(500).optional(),
});
type ProjectInput = z.infer<typeof projectSchema>;

function OnboardingWizard() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);

  function dismiss() {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("onboarding_dismissed", "1");
    }
  }

  function finish() {
    dismiss();
    qc.invalidateQueries({ queryKey: ["me"] });
    navigate({ to: "/dashboard" });
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-ink-50 dark:bg-ink-950 px-4 py-6">
      <div className="w-full max-w-2xl">
        <StepIndicator step={step} />
        {step === 1 && <StepWelcome onNext={() => setStep(2)} onSkip={finish} />}
        {step === 2 && <StepConnect onNext={() => setStep(3)} onBack={() => setStep(1)} />}
        {step === 3 && <StepCreateProject onNext={() => setStep(4)} onBack={() => setStep(2)} />}
        {step === 4 && <StepDone onFinish={finish} />}
      </div>
    </div>
  );
}

function StepIndicator({ step }: { step: number }) {
  return (
    <div className="flex items-center justify-center gap-2 mb-6">
      {[1, 2, 3, 4].map((n) => (
        <div
          key={n}
          className={`h-2 w-12 rounded-full transition-colors ${
            n <= step ? "bg-brand-500" : "bg-ink-200 dark:bg-ink-800"
          }`}
        />
      ))}
    </div>
  );
}

function StepWelcome({ onNext, onSkip }: { onNext: () => void; onSkip: () => void }) {
  const branding = useBranding();
  return (
    <Card>
      <CardHeader>
        <CardTitle>Bienvenido a {branding.brand_name}</CardTitle>
        <CardDescription>
          Vamos a configurar tu monitorización en 3 pasos: conectar tus cuentas, crear tu primer proyecto y empezar a recolectar menciones.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2 text-sm text-ink-600 dark:text-ink-300 mb-6">
          <li className="flex items-center gap-2"><Check size={14} className="text-good" /> Conecta tus redes sociales con OAuth</li>
          <li className="flex items-center gap-2"><Check size={14} className="text-good" /> Define qué monitorear con boolean queries por red</li>
          <li className="flex items-center gap-2"><Check size={14} className="text-good" /> Los workers empiezan a capturar en minutos</li>
        </ul>
        <div className="flex justify-between gap-2">
          <Button variant="outline" onClick={onSkip}>
            <SkipForward size={14} /> Saltar
          </Button>
          <Button onClick={onNext}>Empezar <ArrowRight size={14} /></Button>
        </div>
      </CardContent>
    </Card>
  );
}

function StepConnect({ onNext, onBack }: { onNext: () => void; onBack: () => void }) {
  const providers = useQuery({
    queryKey: ["oauth", "providers"],
    queryFn: () => api.oauth.listProviders(),
    staleTime: 5 * 60_000,
  });
  const profiles = useQuery({
    queryKey: ["profiles"],
    queryFn: () => api.listProfiles(),
    staleTime: 30_000,
  });

  async function connect(provider: string) {
    try {
      const { authorize_url } = await api.oauth.start(provider);
      window.location.href = authorize_url;
    } catch {}
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Paso 1 · Conecta tus redes</CardTitle>
        <CardDescription>Conecta al menos una cuenta para que los workers usen tus credenciales reales. Puedes saltar este paso.</CardDescription>
      </CardHeader>
      <CardContent>
        {providers.isLoading ? (
          <Skeleton className="h-24" />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            {(providers.data ?? []).map((p) => {
              const connected = (profiles.data ?? []).some((pr) => pr.network === p.network);
              return (
                <div key={p.provider} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-ink-200 dark:border-ink-700">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{PROVIDER_LABEL[p.provider] ?? p.provider}</p>
                    <p className="text-[11px] text-ink-500">red: {p.network}</p>
                  </div>
                  {connected ? (
                    <Badge variant="good">conectado</Badge>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!p.configured}
                      onClick={() => connect(p.provider)}
                      title={!p.configured ? "Falta config OAuth en .env" : undefined}
                    >
                      <ExternalLink size={12} /> {p.configured ? "Conectar" : "no config"}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <div className="flex justify-between gap-2">
          <Button variant="outline" onClick={onBack}>Atrás</Button>
          <Button onClick={onNext}>Continuar <ArrowRight size={14} /></Button>
        </div>
      </CardContent>
    </Card>
  );
}

function StepCreateProject({ onNext, onBack }: { onNext: () => void; onBack: () => void }) {
  const create = useCreateProject();
  const form = useForm<ProjectInput>({
    resolver: zodResolver(projectSchema),
    defaultValues: { name: "" },
  });
  const [networks, setNetworks] = useState<string[]>(["x", "news", "yt", "reddit"]);
  const [networkQueries, setNetworkQueries] = useState<Record<string, string>>({});

  async function onSubmit(data: ProjectInput) {
    try {
      await create.mutateAsync({ ...data, networks, network_queries: networkQueries });
      toast.success("Proyecto creado");
      onNext();
    } catch {}
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Paso 2 · Crea tu primer proyecto</CardTitle>
        <CardDescription>Define qué monitorear. La boolean query por red filtra resultados antes del análisis de sentimiento.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
          <div>
            <Label>Nombre del proyecto</Label>
            <Input className="mt-1" placeholder="ej. Marca principal" {...form.register("name")} />
            {form.formState.errors.name && (
              <p className="text-xs text-bad mt-1">{form.formState.errors.name.message}</p>
            )}
          </div>
          <div>
            <Label>Descripción (opcional)</Label>
            <Input className="mt-1" {...form.register("description")} />
          </div>
          <div className="border-t border-ink-200 dark:border-ink-800 pt-3">
            <NetworkQueryEditor
              networks={networks}
              networkQueries={networkQueries}
              onChange={(n, q) => { setNetworks(n); setNetworkQueries(q); }}
            />
          </div>
          <div className="flex justify-between gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onBack}>Atrás</Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? "Creando…" : "Crear proyecto"} <ArrowRight size={14} />
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function StepDone({ onFinish }: { onFinish: () => void }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Listo</CardTitle>
        <CardDescription>Tu proyecto está activo. Los workers empezarán a recolectar menciones en los próximos minutos.</CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2 text-sm text-ink-600 dark:text-ink-300 mb-6">
          <li className="flex items-center gap-2"><Check size={14} className="text-good" /> Twitter / RSS / YouTube / Reddit cada 2-5 minutos</li>
          <li className="flex items-center gap-2"><Check size={14} className="text-good" /> Sentiment automático con Claude</li>
          <li className="flex items-center gap-2"><Check size={14} className="text-good" /> Alertas en tiempo real al dashboard</li>
        </ul>
        <div className="flex justify-end">
          <Button onClick={onFinish}>Ir al dashboard <ArrowRight size={14} /></Button>
        </div>
      </CardContent>
    </Card>
  );
}
